/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  PermissionProtectedRoute,
  type PermissionProtectedRouteProps,
} from "@farmlands/permissions";
import { LoadingLayout } from "@farmlands/ui-library";
import { FC, ReactNode } from "react";
import { useSelector } from "react-redux";

import {
  selectCurrentAccountId,
  selectCurrentFarmerAccount,
  selectIdToken,
} from "../../features/azure-b2c/storage";
import { selectRemoteConfig } from "../../services/firebase/remote-config/remoteConfig.slice";

export type ProtectedRouteProps = Pick<
  PermissionProtectedRouteProps,
  "children" | "redirectTo" | "loadingFallback" | "mode" | "permission" | "permissions"
>;

export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  children,
  redirectTo = "/",
  loadingFallback = <LoadingLayout title="Loading..." />,
  mode,
  permission,
  permissions,
}): ReactNode => {
  const remoteConfig = useSelector(selectRemoteConfig);
  const selectedAccountId = useSelector(selectCurrentAccountId) ?? "";
  const idToken = useSelector(selectIdToken);
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const accountNumber = selectedAccount?.accountNumber ?? "";

  return (
    <PermissionProtectedRoute
      mode={mode}
      permission={permission}
      permissions={permissions}
      accountNumber={accountNumber}
      token={idToken ?? ""}
      b2cAccountId={selectedAccountId}
      basePath={process.env.REACT_APP_PERMISSIONS_API_BASE_PATH ?? ""}
      enabled={remoteConfig.permissionsChecksEnabled}
      redirectTo={redirectTo}
      loadingFallback={loadingFallback}
    >
      {children}
    </PermissionProtectedRoute>
  );
};
